import { Fragment, useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Sparkles, RefreshCw, AlertCircle, Plus, Minus } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  Tile,
  TileContent,
  TileDescription,
  TileFooter,
  TileHeader,
  TileTitle
} from './ui'

interface BillingAggregateRow {
  billingUnitId: string
  costEur: number
  endCustomerPriceEur?: number
  inputTokens: number
  outputTokens: number
  requestCount: number
}

interface MeUsagePayload {
  success: boolean
  hubConfigured: boolean
  tenantId?: string
  period?: { from?: string; to?: string }
  usage: {
    endCustomerPriceEur: number
    llmEndCustomerPriceEur: number
    inputTokens: number
    outputTokens: number
    requestCount: number
    ttsCostEur?: number
    ttsChars?: number
    ttsRequestCount?: number
  } | null
  billingAggregates?: BillingAggregateRow[]
  billingNote?: string
  message?: string
}

interface SchoolSummaryPayload {
  tenantId: string
  endCustomerPriceEur: number
  llmEndCustomerPriceEur: number
  inputTokens: number
  outputTokens: number
  requestCount: number
}

interface SchoolUsagePayload {
  success: boolean
  hubConfigured: boolean
  tenantId?: string
  period?: { from?: string; to?: string }
  summary: SchoolSummaryPayload | null
  billingAggregates?: BillingAggregateRow[]
  areaAggregates?: Array<{
    key: string
    label: string
    endCustomerPriceEur: number
    requestCount: number
    inputTokens: number
    outputTokens: number
  }>
  billingNote?: string
  error?: string
}

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
const num = new Intl.NumberFormat('de-DE')

function billingAggregateLabel(raw: string): string {
  const id = String(raw || '').trim()
  const cw = /^lr:classwork:(\d+)$/.exec(id)
  if (cw) return `Klassenarbeit #${cw[1]}`
  if (id === 'lr:email') return 'E-Mail / Korrespondenz (Assistenz)'
  if (id === 'lr:sonstige') return 'Weitere Assistenz-Nutzung'
  return id
}

function billingGroupKey(raw: string): string {
  const id = String(raw || '').trim()
  if (/^lr:classwork:\d+$/.test(id)) return 'classwork'
  if (id === 'lr:email') return 'email'
  if (id === 'lr:sonstige') return 'sonstige'
  return 'other'
}

function billingGroupLabel(key: string): string {
  if (key === 'classwork') return 'Klassenarbeiten'
  if (key === 'email') return 'E-Mail / Korrespondenz'
  if (key === 'sonstige') return 'Weitere Assistenz-Nutzung'
  return 'Weitere Vorgänge'
}

export function KiHubUsageCard({ title = 'Monatsübersicht Assistenzsysteme' }: { title?: string }) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  const { user } = useAuth()
  const roles = [...(user?.allRoles || []), ...(user?.roles || []), user?.role]
    .filter(Boolean)
    .map((r) => String(r).toLowerCase())
  const canSchool =
    user?.username === 'admin' ||
    roles.some((r) => ['admin', 'administrator', 'schulleitung'].includes(r))

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<MeUsagePayload | SchoolUsagePayload>(
    canSchool ? 'hub-usage-school-card' : 'hub-usage-me',
    async () => {
      const res = await api.get(canSchool ? '/hub/usage/school' : '/hub/usage/me')
      return res.data
    },
    { staleTime: 120_000, retry: 1 }
  )

  if (isLoading) {
    return (
      <Tile>
        <TileContent className="animate-pulse py-6">
          <div className="mb-4 h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-8 w-2/3 rounded bg-gray-100 dark:bg-gray-800" />
        </TileContent>
      </Tile>
    )
  }

  if (isError) {
    return (
      <Tile className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40">
        <TileContent className="flex gap-3 py-5">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <div>
            <p className="font-medium text-amber-900 dark:text-amber-100">Nutzungsübersicht konnte nicht geladen werden</p>
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-200/90">
              {(error as { message?: string })?.message || 'Unbekannter Fehler'}
            </p>
          </div>
        </TileContent>
      </Tile>
    )
  }

  if (!data?.hubConfigured) {
    return (
      <Tile className="border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-900">
        <TileContent className="flex items-start gap-3 py-5">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-slate-500 dark:text-gray-400" aria-hidden />
          <div>
            <TileTitle as="h3" className="text-base">
              {title}
            </TileTitle>
            <TileDescription className="mt-1 text-sm">
              Zentraler Hub ist nicht angebunden (
              <code className="rounded bg-slate-200 px-1 text-xs dark:bg-gray-800 dark:text-gray-200">HUB_BASE_URL</code>
              ). Es werden keine Nutzungsdaten für die Abrechnung übermittelt.
            </TileDescription>
          </div>
        </TileContent>
      </Tile>
    )
  }

  const u =
    canSchool && 'summary' in data
      ? {
          endCustomerPriceEur: data.summary?.endCustomerPriceEur ?? 0,
          llmEndCustomerPriceEur: data.summary?.llmEndCustomerPriceEur ?? 0,
          inputTokens: data.summary?.inputTokens ?? 0,
          outputTokens: data.summary?.outputTokens ?? 0,
          requestCount: data.summary?.requestCount ?? 0,
          ttsChars: 0,
          ttsRequestCount: 0
        }
      : 'usage' in data
        ? data.usage
        : null
  const aggregates = Array.isArray(data.billingAggregates)
    ? data.billingAggregates
    : canSchool && 'areaAggregates' in data && Array.isArray(data.areaAggregates)
      ? data.areaAggregates.map((row) => ({
          billingUnitId: row.label,
          endCustomerPriceEur: row.endCustomerPriceEur,
          costEur: row.endCustomerPriceEur,
          inputTokens: row.inputTokens,
          outputTokens: row.outputTokens,
          requestCount: row.requestCount
        }))
      : []
  const periodHint =
    data.period?.from && data.period?.to
      ? `Monat ${new Date(data.period.from).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}`
      : 'aktueller Monat'
  const grouped = new Map<
    string,
    {
      key: string
      label: string
      totalEur: number
      requestCount: number
      rows: Array<{
        billingUnitId: string
        costEur: number
        endCustomerPriceEur?: number
        inputTokens: number
        outputTokens: number
        requestCount: number
      }>
    }
  >()

  for (const row of aggregates) {
    const key = billingGroupKey(row.billingUnitId)
    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        label: billingGroupLabel(key),
        totalEur: 0,
        requestCount: 0,
        rows: []
      })
    }
    const target = grouped.get(key)!
    target.totalEur += row.endCustomerPriceEur ?? row.costEur ?? 0
    target.requestCount += row.requestCount ?? 0
    target.rows.push(row)
  }

  const groupedAggregates = Array.from(grouped.values()).sort((a, b) => b.totalEur - a.totalEur)

  function toggleGroup(key: string) {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const metricBox =
    'rounded-lg border border-gray-200 bg-gray-50/90 p-4 dark:border-gray-700 dark:bg-gray-800/60'

  return (
    <Tile>
      <TileHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-950/50">
              <Sparkles className="h-5 w-5 text-violet-600 dark:text-violet-300" aria-hidden />
            </div>
            <div className="min-w-0">
              <TileTitle as="h3" className="text-base">
                {title}
              </TileTitle>
              <TileDescription className="mt-0.5">
                Mandant {data.tenantId} · {periodHint} · {canSchool ? 'Schulsumme' : 'Ihr Verbrauch'} (Ihr
                Rechnungsbetrag)
              </TileDescription>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            className="shrink-0 border-violet-300 text-violet-800 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-950/40"
          >
            <RefreshCw className={clsx('mr-2 h-4 w-4', isFetching && 'animate-spin')} aria-hidden />
            Aktualisieren
          </Button>
        </div>
      </TileHeader>

      <TileContent>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className={metricBox}>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Ihr Rechnungsbetrag (gesamt)
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">
              {eur.format(u?.endCustomerPriceEur ?? 0)}
            </p>
          </div>
          <div className={metricBox}>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Davon Text-Assistenz
            </p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50">
              {eur.format(u?.llmEndCustomerPriceEur ?? 0)}
            </p>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{num.format(u?.requestCount ?? 0)} Anfragen</p>
          </div>
          <div className={metricBox}>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Sprachausgabe</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50">
              {num.format(u?.ttsChars ?? 0)} Zeichen
            </p>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{num.format(u?.ttsRequestCount ?? 0)} Anfragen</p>
          </div>
        </div>

        {groupedAggregates.length > 0 ? (
          <div className="mt-6 rounded-lg border border-violet-200 bg-violet-50/50 p-4 dark:border-violet-800 dark:bg-violet-950/25">
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-900 dark:text-violet-200">
              Nach Kostenstelle
            </p>
            <p className="mb-3 mt-1 text-xs text-violet-800/90 dark:text-violet-200/80">
              {data.billingNote ||
                'Eine Klassenarbeit erscheint als eine Zeile (alle Schüler zusammen). Detailnachweise zur Verwaltung stehen im Hub.'}
            </p>
            <TableScrollArea className="shadow-none dark:shadow-none">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell className="text-violet-900 dark:text-violet-200">Kostenstelle</TableHeaderCell>
                    <TableHeaderCell className="text-right text-violet-900 dark:text-violet-200">
                      Rechnungsbetrag
                    </TableHeaderCell>
                    <TableHeaderCell className="text-right text-violet-900 dark:text-violet-200">Anfragen</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedAggregates.map((group) => (
                    <Fragment key={group.key}>
                      <TableRow>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-auto justify-start px-1 py-1 font-medium text-gray-900 hover:bg-violet-100/80 dark:text-gray-100 dark:hover:bg-violet-950/50"
                            onClick={() => toggleGroup(group.key)}
                          >
                            {openGroups[group.key] ? (
                              <Minus className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden />
                            ) : (
                              <Plus className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden />
                            )}
                            {group.label}
                          </Button>
                        </TableCell>
                        <TableCell align="right" className="font-medium">
                          {eur.format(group.totalEur)}
                        </TableCell>
                        <TableCell align="right">{num.format(group.requestCount)}</TableCell>
                      </TableRow>
                      {openGroups[group.key] &&
                        group.rows.map((row) => (
                          <TableRow key={row.billingUnitId} className="bg-violet-50/30 dark:bg-violet-950/20">
                            <TableCell className="pl-8">
                              <span className="text-xs text-gray-600 dark:text-gray-300">
                                {billingAggregateLabel(row.billingUnitId)}
                              </span>
                              <span className="mt-0.5 block font-mono text-[10px] text-gray-500 dark:text-gray-400">
                                {row.billingUnitId}
                              </span>
                            </TableCell>
                            <TableCell align="right" className="text-xs">
                              {eur.format(row.endCustomerPriceEur ?? row.costEur ?? 0)}
                            </TableCell>
                            <TableCell align="right" className="text-xs">
                              {num.format(row.requestCount ?? 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableScrollArea>
          </div>
        ) : null}
      </TileContent>

      {canSchool ? (
        <TileFooter>
          <Link
            to="/admin/kontostand"
            className="text-sm font-medium text-violet-700 hover:underline dark:text-violet-300 dark:hover:text-violet-200"
          >
            Schulübersicht & Nutzeraufschlüsselung →
          </Link>
        </TileFooter>
      ) : null}
    </Tile>
  )
}
